import React, { useEffect, useMemo } from 'react';
import {
    BlockComponent,
    ComponentSettings,
    ComponentSettingsManageFilters,
    ContentSettings,
    contentSettingsToComponentProperties,
    ManageFiltersInitialDataResponse,
    RendererFilter,
} from '@report-content';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FilterControl from 'components/report-content/components/manage-filters/parts/filter/FilterControl';
import { buildFilterColumn } from 'components/report-content/components/manage-filters/helpers/buildFilterColumns';
import buildDataQueries from 'components/report-content/components/manage-filters/helpers/buildDataQueries';
import { IFilterColumn } from 'components/report-content/components/manage-filters/index';
import { reportAPI } from 'api/report';
import { Box, Skeleton } from '@mui/material';

export default function FieldDefaultsControl({
    currentFieldState,
    contentSettings,
    component,
    onChange,
}: {
    currentFieldState: RendererFilter;
    contentSettings: ContentSettings;
    component: BlockComponent<ComponentSettingsManageFilters>;
    onChange: (filter: IFilterColumn, value: any) => void;
}) {
    const queryClient = useQueryClient();
    const initialQueryKey = [
        'report_block_initial',
        component.datasetRendererBlockComponentId,
        currentFieldState.reference_name,
        currentFieldState.filterType,
        currentFieldState.use_external_filter_value,
    ].join('_');

    const removeData = () => queryClient.removeQueries({ queryKey: [initialQueryKey] });
    const { data, isRefetching, isLoading } = useQuery<ManageFiltersInitialDataResponse, Error>({
        queryKey: [initialQueryKey],
        queryFn: () => {
            const fields = structuredClone(component.settings.fields);
            const index = fields.findIndex((f) => f.reference_name == currentFieldState.reference_name);
            if (index != -1) {
                fields[index] = currentFieldState;
                fields[index].filter_column = 'Y';
            }
            return reportAPI.getComponentData(
                {
                    ...component.settings,
                    dataQueries: buildDataQueries(fields, component.settings.only_display_relevant_values == 'Y'),
                    fields: fields,
                } as ComponentSettings,
                { ...contentSettingsToComponentProperties(contentSettings), page: 0 },
                null,
            );
        },
    });

    useEffect(() => {
        return () => removeData();
    }, []);

    const filterColumn = useMemo(() => {
        if (!data) {
            return false;
        }
        const field = buildFilterColumn(currentFieldState, data);
        field.savedValues = field.filter_default_value ?? field.savedValues;
        if (field.filterType == 'range') {
            if (field.savedValues.length != 2) {
                field.savedValues = [field.data['from'], field.data['to']];
            }
        } else if (field.filterType == 'charting_interval' || field.filterType == 'dropdown') {
            if (field.savedValues.length > 1) {
                field.savedValues = [field.savedValues[0]];
            }
        }
        return field;
    }, [currentFieldState, data]);

    if (isLoading || isRefetching || !filterColumn) {
        return (
            <Box>
                <Skeleton height={'28px'} variant="rectangular" animation={'pulse'} />
            </Box>
        );
    }
    return <FilterControl filter={filterColumn} handleFilterChange={onChange} />;
}
